import type { ReactElement, ReactNode } from 'react';
import { CardBackGlyph, CardBackPattern } from './CardBackGlyph.js';
import { CardInfoCard, type CardInfoData } from './CardInfoCard.js';
import { useCardTilt } from './useCardTilt.js';

/** Canonical full-card footprint; dialogs magnify it without changing its shape. */
export const CATALOG_CARD_WIDTH = 215;
export const CATALOG_CARD_HEIGHT = 338;
/**
 * Silhouette footprint for "a card waits here" slots: same proportion as the
 * canonical card, small enough to stay quieter than the surrounding actions.
 */
export const MINI_CATALOG_CARD_WIDTH = 40;
export const MINI_CATALOG_CARD_HEIGHT = 63;
/**
 * Grid track / hover-preview width. Catalog tiles, the deck-editor slots' hover
 * preview, the battle hover popover and click dialog all share this footprint
 * so a card keeps one consistent proportion on every surface.
 */
export const CATALOG_CARD_MIN_WIDTH = CATALOG_CARD_WIDTH;
export const CATALOG_CARD_MOBILE_MIN_WIDTH = 150;

export interface CatalogCardProps {
  data: CardInfoData;
  testId: string;
  footer?: ReactNode;
  /** Accessible ids forwarded to the inner panel when the card titles a dialog. */
  titleId?: string;
  descriptionId?: string;
}

/** Shared fixed-size card used by the public catalog, deck editor and battle previews. */
export function CatalogCard({
  data,
  testId,
  footer,
  titleId,
  descriptionId,
}: CatalogCardProps): ReactElement {
  return (
    <article
      className={`cards-catalog-card${footer === undefined ? '' : ' cards-catalog-card-with-footer'}`}
      data-footprint="standard"
    >
      <CardInfoCard
        data={data}
        density="compact"
        keywordDisplay="collapsed"
        testId={testId}
        {...(titleId === undefined ? {} : { titleId })}
        {...(descriptionId === undefined ? {} : { descriptionId })}
      />
      {footer === undefined ? null : <div className="cards-catalog-card-footer">{footer}</div>}
    </article>
  );
}

export interface CatalogCardBackProps {
  testId: string;
  ariaLabel: string;
  footprint?: 'standard' | 'mini';
}

/** Canonical face-down card with the same footprint, tilt and glare as a card face. */
export function CatalogCardBack({
  testId,
  ariaLabel,
  footprint = 'standard',
}: CatalogCardBackProps): ReactElement {
  const tilt = useCardTilt();
  return (
    <article className="cards-catalog-card" data-footprint={footprint}>
      <div
        className="cards-card-detail-panel cards-card-detail-compact cards-catalog-card-back cards-tilt"
        data-testid={testId}
        aria-label={ariaLabel}
        role="img"
        {...tilt}
      >
        <span className="cards-tilt-glare" aria-hidden="true" />
        <CardBackPattern
          id={`${testId}-back-pattern`}
          testId={`${testId}-pattern`}
          className="cards-card-detail-pattern cards-catalog-card-back-pattern"
        />
        <div className="cards-catalog-card-back-art" aria-hidden="true">
          <CardBackGlyph size={112} />
        </div>
      </div>
    </article>
  );
}

export const CATALOG_CARD_STYLES = `
  .cards-catalog-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(${CATALOG_CARD_MIN_WIDTH}px, 1fr)); gap: 16px; align-items: stretch; }
  .cards-catalog-card { --cards-catalog-card-height: ${CATALOG_CARD_HEIGHT}px; min-width: 0; display: grid; grid-template-rows: var(--cards-catalog-card-height); }
  .cards-catalog-card[data-footprint="mini"] { --cards-catalog-card-height: ${MINI_CATALOG_CARD_HEIGHT}px; width: ${MINI_CATALOG_CARD_WIDTH}px; }
  .cards-catalog-card[data-footprint="mini"] > .cards-catalog-card-back { border-width: var(--stroke-m); border-radius: var(--radius-xs); box-shadow: 0 var(--step-1) 0 var(--c-step-shadow); }
  .cards-catalog-card[data-footprint="mini"] > .cards-catalog-card-back::after { display: none; }
  .cards-catalog-card-with-footer { grid-template-rows: var(--cards-catalog-card-height) auto; gap: 8px; }
  .cards-catalog-card > .cards-card-detail-panel { min-width: 0; height: var(--cards-catalog-card-height); display: flex; flex-direction: column; overflow: hidden; }
  /* touch-action pins the text column to vertical panning: the tilt on the
     panel above it only claims taps (useCardTilt skips scrollable areas), and a
     finger dragged across the description must always reach this scroller. */
  .cards-catalog-card .cards-card-detail-scroll-content { min-height: 0; flex: 1 1 auto; overflow-y: auto; overscroll-behavior: auto; touch-action: pan-y; scrollbar-width: thin; scrollbar-color: var(--c-panel-edge) transparent; }
  .cards-catalog-card .cards-card-detail-scroll-content::-webkit-scrollbar { width: 5px; }
  .cards-catalog-card .cards-card-detail-scroll-content::-webkit-scrollbar-thumb { border-radius: var(--radius-round); background: var(--c-panel-edge); }
  .cards-catalog-card-footer { position: relative; z-index: 1; min-width: 0; width: 100%; }
  /* The card back (01 §10, 02 §3.2) at the full footprint: dark felt, its
     diamond weave, a gold frame and the gold mark. */
  .cards-catalog-card > .cards-catalog-card-back {
    display: grid;
    place-items: center;
    padding: 0;
    border: var(--stroke-l) solid var(--c-outline);
    border-radius: var(--radius-xl);
    background: var(--c-card-back);
  }
  .cards-catalog-card > .cards-catalog-card-back::after {
    content: '';
    position: absolute;
    z-index: 1;
    inset: var(--space-2);
    border: var(--stroke-l) solid var(--c-card-back-mark);
    border-radius: var(--radius-s);
    pointer-events: none;
  }
  /* The weave at full strength, as on every back (01 §10): the detail panel's
     pattern is a 15% backdrop, a back's is the back itself. */
  .cards-catalog-card > .cards-catalog-card-back > .cards-catalog-card-back-pattern { opacity: 1; }
  .cards-catalog-card-back-art { display: grid; place-items: center; width: 100%; height: 100%; }
  .cards-catalog-card-back-art svg { width: 54px; height: 54px; }
  .cards-catalog-card[data-footprint="mini"] .cards-catalog-card-back-art svg { width: 18px; height: 18px; }
  @media (max-width: 620px) {
    /* Opt-in portrait catalog layout shared with the deck editor: two narrow
       tracks keep cards from stretching into landscape tiles on phones. */
    .cards-catalog-grid-mobile-portrait { grid-template-columns: repeat(auto-fill, minmax(${CATALOG_CARD_MOBILE_MIN_WIDTH}px, 1fr)); gap: 10px; }
    .cards-catalog-grid-mobile-portrait .cards-catalog-card { grid-template-rows: auto; }
    .cards-catalog-grid-mobile-portrait .cards-catalog-card-with-footer { grid-template-rows: auto auto; }
    .cards-catalog-grid-mobile-portrait .cards-catalog-card > .cards-card-detail-panel { height: auto; aspect-ratio: ${CATALOG_CARD_MIN_WIDTH} / ${CATALOG_CARD_HEIGHT}; }
    /* A two-up card is ~150px wide: the art window drops to the 60px scale so
       the rules text keeps a few lines. */
    .cards-catalog-grid-mobile-portrait .cards-card-detail-header > svg { height: calc(60px + 2 * var(--space-1)); }
  }
`;
